import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

import RequireAuth from "../services/RequireAuth";
import RequireAI from "../services/RequireAI";

import AIWorkspace from "./AIWorkspace";
import AILogin from "./AILogin";
import Logout from "../pages/login/Logout";

import AIShell from "./shell/AIShell";
import AIOverview from "./pages/AIOverview";
import { AIDealsList, AIDealHome } from "./pages/AIDeals";
import { AIBuyers, AIBuyerDetail } from "./pages/AIBuyers";
import { AIContracts, AIContractDetail } from "./pages/AIContracts";
import { AIDealExecution } from "./pages/AIDealExecution";
import { AIDealCommandCenter } from "./pages/AIDealCommandCenter";
import AIIntelligence from "./pages/AIIntelligence";
import AIRisks from "./pages/AIRisks";
import AIObligations from "./pages/AIObligations";
import AIContractsPage from "./pages/AIContractsPage";
import AIActionsPage from "./pages/AIActionsPage";
import AIActivityPage from "./pages/AIActivityPage";
import AISettings from "./pages/AISettings";
import Onboard from "./pages/Onboard";
import DealSetup from "./pages/DealSetup";
import DealWorkspace from "./pages/DealWorkspace";

import "./ai.css";

// Step 18 — Full AI application shell.
//
// Route tree:
//   /login, /logout                     unauthenticated (or session-only)
//   /                                   AIShell → AIOverview
//   /deals                              AIShell → deals list
//   /deals/:workspaceId                 AIShell → deal home
//   /deals/:workspaceId/buyers          AIShell → buyer portfolio
//   /deals/:workspaceId/buyers/:acqId   AIShell → per-buyer intelligence
//   /deals/:workspaceId/intelligence    AIShell → intelligence snapshot
//   /deals/:workspaceId/risks           AIShell → known risks
//   /deals/:workspaceId/obligations     AIShell → placeholder (no backend)
//   /deals/:workspaceId/contracts       AIShell → contract intelligence
//   /deals/:workspaceId/contracts/review  AIShell → human-review panel
//                                       (approve / send / sign etc.)
//   /deals/:workspaceId/contracts/:cid  AIShell → per-contract intelligence
//   /deals/:workspaceId/actions         AIShell → deal actions panel
//   /deals/:workspaceId/execution       AIShell → execution & closing
//                                       readiness workspace (Step 24)
//   /deals/:workspaceId/command         AIShell → deal command center
//                                       (Step 25 orchestration surface)
//   /deals/:workspaceId/activity        AIShell → snapshot + notif log
//   /settings                           AIShell → account settings link-out
//   /chat, /chat/:id                    conversation-first workspace
//                                       (kept as-is — Step 18 does not
//                                       rewrite the chat UI)
//   /c/:id                              legacy redirect → /chat/:id
//
// Step-17 gating: every protected route sits under
// RequireAuth → RequireAI. Chat routes get the same wrapping so a
// broker who lands on ai.<host>/chat is also bounced.

function LegacyConversationRedirect() {
  const { id } = useParams();
  return <Navigate to={`/chat/${encodeURIComponent(id)}`} replace />;
}

export default function AIApp() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<AILogin />} />
        <Route path="/logout" element={<Logout />} />

        {/* Pre-registration onboarding wizard — anonymous. */}
        <Route path="/onboard" element={<Onboard />} />

        {/* Post-registration deal shell — three-column workspace +
            full-screen onboarding conversation. Both require an AI
            session; RequireAI bounces broker/buyer accounts. */}
        <Route
          path="/deal/:id/setup"
          element={
            <RequireAuth>
              <RequireAI>
                <DealSetup />
              </RequireAI>
            </RequireAuth>
          }
        />
        <Route
          path="/deal/:id"
          element={
            <RequireAuth>
              <RequireAI>
                <DealWorkspace />
              </RequireAI>
            </RequireAuth>
          }
        />

        {/* Shell-hosted routes */}
        <Route
          element={
            <RequireAuth>
              <RequireAI>
                <AIShell />
              </RequireAI>
            </RequireAuth>
          }
        >
          <Route index element={<AIOverview />} />
          <Route path="deals" element={<AIDealsList />} />
          <Route path="deals/:workspaceId" element={<AIDealHome />} />
          <Route path="deals/:workspaceId/buyers" element={<AIBuyers />} />
          <Route
            path="deals/:workspaceId/buyers/:acquirerId"
            element={<AIBuyerDetail />}
          />
          <Route
            path="deals/:workspaceId/intelligence"
            element={<AIIntelligence />}
          />
          <Route path="deals/:workspaceId/risks" element={<AIRisks />} />
          <Route
            path="deals/:workspaceId/obligations"
            element={<AIObligations />}
          />
          <Route
            path="deals/:workspaceId/contracts"
            element={<AIContracts />}
          />
          <Route
            path="deals/:workspaceId/contracts/review"
            element={<AIContractsPage />}
          />
          <Route
            path="deals/:workspaceId/contracts/:contractDraftId"
            element={<AIContractDetail />}
          />
          <Route
            path="deals/:workspaceId/actions"
            element={<AIActionsPage />}
          />
          <Route
            path="deals/:workspaceId/execution"
            element={<AIDealExecution />}
          />
          <Route
            path="deals/:workspaceId/command"
            element={<AIDealCommandCenter />}
          />
          <Route
            path="deals/:workspaceId/activity"
            element={<AIActivityPage />}
          />
          <Route path="settings" element={<AISettings />} />
        </Route>

        {/* Conversation-first workspace (kept as-is, Step 1-15 UI) */}
        <Route
          path="chat"
          element={
            <RequireAuth>
              <RequireAI>
                <AIWorkspace />
              </RequireAI>
            </RequireAuth>
          }
        />
        <Route
          path="chat/:id"
          element={
            <RequireAuth>
              <RequireAI>
                <AIWorkspace />
              </RequireAI>
            </RequireAuth>
          }
        />

        {/* Back-compat for existing bookmarks. */}
        <Route path="c/:id" element={<LegacyConversationRedirect />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
